/**
 * Interactive Pie Chart Card
 * Generic reusable component with chart + table layout
 * Table rows act as interactive legend - click to toggle, hover to highlight
 */

import { useMemo, useState, useRef } from "react";
import type { ReactNode } from "react";
import ReactECharts from "echarts-for-react";
import type EChartsReact from "echarts-for-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// ECharts default color palette
const DEFAULT_CHART_COLORS = [
  "#5470c6", // Blue
  "#91cc75", // Green
  "#fac858", // Yellow
  "#ee6666", // Red
  "#73c0de", // Light Blue
  "#3ba272", // Dark Green
  "#fc8452", // Orange
  "#9a60b4", // Purple
  "#ea7ccc", // Pink
];

export interface PieChartDataItem {
  /** Display label for the segment */
  label: string;
  /** Numeric value */
  value: number;
  /** Optional custom color (uses default palette if not provided) */
  color?: string;
}

export interface InteractivePieChartCardProps {
  /** Card title */
  title: string;
  /** Optional subtitle/description */
  subtitle?: string;
  /** Data items for the chart and table */
  data: PieChartDataItem[];
  /** Loading state */
  isLoading?: boolean;
  /** Custom color palette (uses default if not provided) */
  colors?: string[];
  /** Chart series name (for tooltips) */
  seriesName?: string;
  /** Show percentage column in table */
  showPercentage?: boolean;
  /** Show total row in table */
  showTotal?: boolean;
  /** Custom table column headers */
  tableHeaders?: {
    label?: string;
    value?: string;
    percentage?: string;
  };
  /** Chart height (default: 350px) */
  chartHeight?: string;
  /** Format value display (e.g., add currency, units) */
  formatValue?: (value: number) => string;
  /** Custom tooltip formatter */
  tooltipFormatter?: string;
  /** Chart radius (inner and outer) */
  chartRadius?: [string, string];
  /** Additional columns appended to the right of the table (for comparisons) */
  extraColumns?: {
    header: string;
    /** Render cell for a data row. Receives label and value of that row. */
    getValue: (label: string, value: number) => ReactNode;
    /** Render cell for the TOTAL row. Optional — shows empty if not provided. */
    getTotalValue?: (labels: string[], values: number[]) => ReactNode;
  }[];
  /**
   * Chart-to-table width ratio on large screens.
   * - `"equal"` (default) — 50 / 50
   * - `"wide-table"` — chart gets ~37 %, table gets ~63 %
   */
  layout?: "equal" | "wide-table";
  /** Custom class for the card */
  className?: string;
}

export function InteractivePieChartCard({
  title,
  subtitle,
  data,
  isLoading = false,
  colors = DEFAULT_CHART_COLORS,
  seriesName = "Data",
  showPercentage = true,
  showTotal = true,
  tableHeaders = {
    label: "Categoría",
    value: "Cantidad",
    percentage: "Porcentaje",
  },
  chartHeight = "350px",
  formatValue = (value) => value.toString(),
  tooltipFormatter = "{b}: {c} ({d}%)",
  chartRadius = ["40%", "70%"],
  extraColumns = [],
  layout = "equal",
  className = "",
}: InteractivePieChartCardProps) {
  const chartRef = useRef<EChartsReact | null>(null);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

  // Calculate breakdown with percentages
  const breakdown = useMemo(() => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    return data.map((item, index) => ({
      label: item.label,
      value: item.value,
      percentage: total > 0 ? ((item.value / total) * 100).toFixed(1) : "0.0",
      color: item.color || colors[index % colors.length],
    }));
  }, [data, colors]);

  const total = data.reduce((sum, item) => sum + item.value, 0);

  // Toggle item visibility
  const handleRowClick = (label: string) => {
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(label)) {
        newSet.delete(label);
      } else {
        newSet.add(label);
      }
      return newSet;
    });

    // Trigger chart animation
    if (chartRef.current) {
      const chart = chartRef.current.getEchartsInstance();
      chart.dispatchAction({
        type: "legendToggleSelect",
        name: label,
      });
    }
  };

  // Highlight chart segment on row hover
  const handleRowHover = (label: string, isEntering: boolean) => {
    if (chartRef.current) {
      const chart = chartRef.current.getEchartsInstance();
      chart.dispatchAction({
        type: isEntering ? "highlight" : "downplay",
        seriesName,
        name: label,
      });
    }
  };

  // ECharts configuration for pie chart
  const chartOption = useMemo(
    () => ({
      tooltip: {
        trigger: "item",
        formatter: tooltipFormatter,
      },
      legend: {
        show: false, // Hide built-in legend - table acts as legend
      },
      series: [
        {
          name: seriesName,
          type: "pie",
          radius: chartRadius,
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: "hsl(var(--background))",
            borderWidth: 2,
          },
          label: {
            show: false,
            position: "center",
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 20,
              fontWeight: "bold",
              formatter: "{b}\n{d}%",
            },
          },
          labelLine: {
            show: false,
          },
          data: breakdown.map((item) => ({
            name: item.label,
            value: item.value,
            itemStyle: {
              color: item.color,
              opacity: selectedItems.has(item.label) ? 0.3 : 1,
            },
          })),
        },
      ],
    }),
    [breakdown, selectedItems, seriesName, tooltipFormatter, chartRadius]
  );

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
        </CardHeader>
        <CardContent>
          <div className="h-96 animate-pulse rounded bg-muted" />
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
        </CardHeader>
        <CardContent>
          <div className="flex h-64 items-center justify-center text-muted-foreground">
            No hay datos disponibles
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </CardHeader>
      <CardContent>
        <div
          className={`grid gap-6 md:grid-cols-1 ${
            layout === "wide-table"
              ? "lg:grid-cols-[3fr_5fr]"
              : "lg:grid-cols-2"
          }`}
        >
          {/* Left: Pie Chart */}
          <div className="flex items-center justify-center">
            <ReactECharts
              ref={(e) => {
                if (e) chartRef.current = e;
              }}
              option={chartOption}
              style={{ height: chartHeight, width: "100%", minHeight: "300px" }}
              opts={{ renderer: "svg" }}
            />
          </div>

          {/* Right: Interactive Table (acts as legend) */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{tableHeaders.label}</TableHead>
                  <TableHead className="text-right">{tableHeaders.value}</TableHead>
                  {showPercentage && (
                    <TableHead className="text-right">{tableHeaders.percentage}</TableHead>
                  )}
                  {extraColumns.map((col, i) => (
                    <TableHead key={i} className="text-right">{col.header}</TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {breakdown.map((item, idx) => {
                  const isHidden = selectedItems.has(item.label);
                  return (
                    <TableRow
                      key={idx}
                      onClick={() => handleRowClick(item.label)}
                      onMouseEnter={() => handleRowHover(item.label, true)}
                      onMouseLeave={() => handleRowHover(item.label, false)}
                      className={`cursor-pointer transition-opacity hover:bg-muted/50 ${
                        isHidden ? "opacity-40" : ""
                      }`}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <div
                            className={`size-3 shrink-0 rounded-full transition-opacity ${
                              isHidden ? "opacity-40" : ""
                            }`}
                            style={{ backgroundColor: item.color }}
                          />
                          <span className={isHidden ? "line-through" : ""}>{item.label}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">{formatValue(item.value)}</TableCell>
                      {showPercentage && (
                        <TableCell className="text-right">{item.percentage}%</TableCell>
                      )}
                      {extraColumns.map((col, i) => (
                        <TableCell key={i} className="text-right">
                          {col.getValue(item.label, item.value)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
                {showTotal && breakdown.length > 0 && (
                  <TableRow className="border-t-2 font-semibold">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="size-3 shrink-0" />
                        TOTAL
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{formatValue(total)}</TableCell>
                    {showPercentage && <TableCell className="text-right">100%</TableCell>}
                    {extraColumns.map((col, i) => (
                      <TableCell key={i} className="text-right">
                        {col.getTotalValue
                          ? col.getTotalValue(
                              breakdown.map((b) => b.label),
                              breakdown.map((b) => b.value)
                            )
                          : null}
                      </TableCell>
                    ))}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
